import { useMemo, useRef, useState } from "react";
import shotChartImage from "../../../images/shotchart.jpg";

function ShotChartCourt({ shots }) {
    const [hoveredShot, setHoveredShot] = useState(null);
    const [tooltipPosition, setTooltipPosition] = useState({ x: 0, y: 0 });
    const containerRef = useRef(null);

    const court = {
        left: 4,
        top: 3,
        width: 92,
        height: 54,
    };

    const handleShotMouseMove = (event, shot) => {
        if (!containerRef.current) {
            return;
        }

        const rect = containerRef.current.getBoundingClientRect();
        setTooltipPosition({
            x: event.clientX - rect.left,
            y: event.clientY - rect.top,
        });
        setHoveredShot(shot);
    };

    const handleShotTouch = (event, shot) => {
        event.preventDefault();
        if (!containerRef.current) return;
        const touch = event.touches[0];
        if (!touch) return;

        const rect = containerRef.current.getBoundingClientRect();
        setTooltipPosition({
            x: touch.clientX - rect.left,
            y: touch.clientY - rect.top,
        });
        setHoveredShot(shot);
    };

    const hideTooltip = () => {
        setHoveredShot(null);
    };

    return (
        <div ref={containerRef} className="relative rounded-xl border border-white/10 bg-[#062a1a] p-3">
            <div className="relative w-full overflow-hidden rounded-lg border border-white/10 bg-[#08160f]">
                <img src={shotChartImage} alt="Cancha de basketball" className="w-full h-auto block" />

                <svg
                    viewBox="0 0 100 60"
                    className="absolute inset-0 w-full h-full"
                    role="img"
                    aria-label="Ubicación de tiros sobre cancha de basketball"
                    onMouseLeave={hideTooltip}
                >
                    {shots.map((shot) => {
                        const x = court.left + ((shot.x - 4) / 92) * court.width;
                        const y = court.top + ((shot.y - 4) / 88) * court.height;

                        return (
                            <g key={shot.id}>
                                <circle
                                    cx={x}
                                    cy={y}
                                    r="3"
                                    fill="transparent"
                                    onMouseEnter={(event) => handleShotMouseMove(event, shot)}
                                    onMouseMove={(event) => handleShotMouseMove(event, shot)}
                                    onMouseLeave={hideTooltip}
                                    onTouchStart={(event) => handleShotTouch(event, shot)}
                                    onTouchEnd={hideTooltip}
                                    style={{ cursor: "pointer" }}
                                />
                                <circle
                                    cx={x}
                                    cy={y}
                                    r="1.1"
                                    fill={shot.isMade ? "#22c55e" : "#ef4444"}
                                    stroke="rgba(0,0,0,0.5)"
                                    strokeWidth="0.2"
                                    pointerEvents="none"
                                />
                            </g>
                        );
                    })}
                </svg>
            </div>

            {hoveredShot ? (
                <div
                    className="pointer-events-none absolute z-20 px-2.5 py-1.5 rounded-md border border-white/10 bg-[#0b1b12]/95 text-slate-100 text-xs font-semibold whitespace-nowrap"
                    style={{
                        left: `${tooltipPosition.x}px`,
                        top: `${tooltipPosition.y - 10}px`,
                        transform: "translate(-50%, -100%)",
                    }}
                >
                    {`${hoveredShot.player || "Jugador"} • ${hoveredShot.actionType || "Tiro"}`}
                </div>
            ) : null}
        </div>
    );
}

function ComparisonBar({
    label,
    firstValue,
    secondValue,
    firstTextColorClass,
    secondTextColorClass,
    firstBarColorClass,
    secondBarColorClass,
}) {
    const total = Math.max(firstValue + secondValue, 1);
    const firstPercent = (firstValue / total) * 100;
    const secondPercent = (secondValue / total) * 100;

    return (
        <div>
            <div className="flex items-center justify-between mb-1.5 text-sm font-semibold">
                <span className="text-slate-300">{label}</span>
                <div className="flex items-center gap-3 text-sm font-black tabular-nums">
                    <span className={firstTextColorClass}>{firstValue}</span>
                    <span className={secondTextColorClass}>{secondValue}</span>
                </div>
            </div>
            <div className="h-1.5 rounded-full overflow-hidden flex bg-[#0b1b12] border border-white/10">
                <div className={firstBarColorClass} style={{ width: `${firstPercent}%` }} />
                <div className={secondBarColorClass} style={{ width: `${secondPercent}%` }} />
            </div>
        </div>
    );
}

function PartidoShotChartSection({ shotChart, rivalLabel = "RIVAL", aguadaWon = true }) {
    const [teamFilter, setTeamFilter] = useState("all");
    const [periodFilter, setPeriodFilter] = useState("all");
    const [resultFilter, setResultFilter] = useState({ made: false, missed: false });
    const allShots = useMemo(() => shotChart?.shots || [], [shotChart]);
    const periodOptions = useMemo(() => {
        const uniquePeriods = [...new Set(
            allShots
                .map((shot) => shot?.period)
                .filter((period) => Number.isFinite(period) && period > 0)
                .map((period) => Math.trunc(period))
        )].sort((a, b) => a - b);

        const regulationPeriods = [1, 2, 3, 4];
        const overtimePeriods = uniquePeriods.filter((period) => period > 4);
        const availablePeriods = [...regulationPeriods, ...overtimePeriods];

        return [{ key: "all", label: "PARTIDO" }, ...availablePeriods.map((period) => ({
            key: String(period),
            label: period <= 4 ? `Q${period}` : `TE${period - 4}`,
        }))];
    }, [allShots]);

    const filteredShots = useMemo(() => {
        let shots = allShots;

        if (periodFilter !== "all") {
            const selectedPeriod = Number(periodFilter);
            shots = shots.filter((shot) => shot.period === selectedPeriod);
        }

        if (teamFilter !== "all") {
            shots = shots.filter((shot) => shot.team === teamFilter);
        }

        const noResultSelected = !resultFilter.made && !resultFilter.missed;
        if (!noResultSelected) {
            shots = shots.filter((shot) => (shot.isMade ? resultFilter.made : resultFilter.missed));
        }

        return shots;
    }, [allShots, periodFilter, teamFilter, resultFilter]);

    const isAguadaHome = shotChart?.isAguadaHome ?? true;
    const aguadaFgPercent = shotChart?.summary?.aguadaFgPercent || 0;
    const rivalFgPercent = shotChart?.summary?.rivalFgPercent || 0;
    const pointsInPaintAguada = shotChart?.summary?.pointsInPaint?.aguada || 0;
    const pointsInPaintRival = shotChart?.summary?.pointsInPaint?.rival || 0;
    const pointsFromTurnoversAguada = shotChart?.summary?.pointsFromTurnovers?.aguada || 0;
    const pointsFromTurnoversRival = shotChart?.summary?.pointsFromTurnovers?.rival || 0;
    const secondChancePointsAguada = shotChart?.summary?.secondChancePoints?.aguada || 0;
    const secondChancePointsRival = shotChart?.summary?.secondChancePoints?.rival || 0;
    const fastBreakAguada = shotChart?.summary?.fastBreakPoints?.aguada || 0;
    const fastBreakRival = shotChart?.summary?.fastBreakPoints?.rival || 0;
    const benchPointsAguada = shotChart?.summary?.benchPoints?.aguada || 0;
    const benchPointsRival = shotChart?.summary?.benchPoints?.rival || 0;
    const firstTeamLabel = isAguadaHome ? "Aguada" : rivalLabel;
    const firstFgPercent = isAguadaHome ? aguadaFgPercent : rivalFgPercent;
    const secondTeamLabel = isAguadaHome ? rivalLabel : "Aguada";
    const secondFgPercent = isAguadaHome ? rivalFgPercent : aguadaFgPercent;
    const aguadaTextColorClass = aguadaWon ? "text-green-400" : "text-red-400";
    const rivalTextColorClass = aguadaWon ? "text-red-400" : "text-green-400";
    const aguadaBarColorClass = aguadaWon ? "bg-green-500" : "bg-red-500";
    const rivalBarColorClass = aguadaWon ? "bg-red-500" : "bg-green-500";
    const firstFgColor = isAguadaHome ? aguadaTextColorClass : rivalTextColorClass;
    const secondFgColor = isAguadaHome ? rivalTextColorClass : aguadaTextColorClass;
    const firstTextColorClass = isAguadaHome ? aguadaTextColorClass : rivalTextColorClass;
    const secondTextColorClass = isAguadaHome ? rivalTextColorClass : aguadaTextColorClass;
    const firstBarColorClass = isAguadaHome ? aguadaBarColorClass : rivalBarColorClass;
    const secondBarColorClass = isAguadaHome ? rivalBarColorClass : aguadaBarColorClass;
    const byOrder = (aguadaValue, rivalValue) => (
        isAguadaHome
            ? { first: aguadaValue, second: rivalValue }
            : { first: rivalValue, second: aguadaValue }
    );

    return (
        <section className="bg-[#102216] rounded-xl p-3 sm:p-4 md:p-6 border border-white/10 mb-8">
            <div className="flex flex-col gap-3 sm:gap-4 mb-4 sm:mb-5">
                <h3 className="text-lg sm:text-2xl font-bold flex items-center gap-2 text-slate-100">
                    <span className="w-1.5 sm:w-2 h-6 sm:h-8 bg-green-500 rounded-full" />
                    Carta de tiro
                </h3>

                {allShots.length > 0 && <div className="flex flex-wrap gap-2">
                    <div className="inline-flex items-center bg-[#0b1b12] border border-white/10 rounded-xl overflow-x-auto">
                    <button
                        type="button"
                        onClick={() => setTeamFilter("all")}
                        className={`px-3 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-xs font-bold tracking-[0.12em] rounded-lg whitespace-nowrap ${teamFilter === "all" ? "bg-green-500 text-[#062a1a]" : "text-slate-300 hover:text-white"}`}
                    >
                        AMBOS
                    </button>
                    <button
                        type="button"
                        onClick={() => setTeamFilter("aguada")}
                        className={`px-3 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-xs font-bold tracking-[0.12em] rounded-lg whitespace-nowrap ${teamFilter === "aguada" ? "bg-green-500 text-[#062a1a]" : "text-slate-300 hover:text-white"}`}
                    >
                        AGUADA
                    </button>
                    <button
                        type="button"
                        onClick={() => setTeamFilter("rival")}
                        className={`px-3 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-xs font-bold tracking-[0.12em] rounded-lg whitespace-nowrap ${teamFilter === "rival" ? "bg-green-500 text-[#062a1a]" : "text-slate-300 hover:text-white"}`}
                    >
                        {rivalLabel}
                    </button>
                </div>

                    <div className="inline-flex items-center bg-[#0b1b12] border border-white/10 rounded-xl overflow-x-auto">
                    {periodOptions.map((option) => (
                        <button
                            key={option.key}
                            type="button"
                            onClick={() => setPeriodFilter(option.key)}
                            className={`px-3 sm:px-4 py-1.5 sm:py-2 text-[10px] sm:text-xs font-bold tracking-[0.12em] rounded-lg whitespace-nowrap ${periodFilter === option.key ? "bg-green-500 text-[#062a1a]" : "text-slate-300 hover:text-white"}`}
                        >
                            {option.label}
                        </button>
                    ))}
                </div>
                </div>}
            </div>

            {!shotChart?.hasData ? (
                <div className="rounded-lg border border-white/10 bg-[#0f1f15] p-6 text-slate-300 text-sm text-center">
                    No hay datos de tiros para mostrar en este partido.
                </div>
            ) : (
                <div className="grid grid-cols-1 xl:grid-cols-[1.15fr_1fr] gap-4 sm:gap-6">
                    <ShotChartCourt shots={filteredShots} />

                    <aside className="space-y-4 sm:space-y-5">
                        <div className="grid grid-cols-2 gap-2 sm:gap-3">
                            <div className="rounded-xl border border-white/10 bg-[#0f1f15] p-3 sm:p-4 text-center">
                                <p className={`text-2xl sm:text-4xl font-black tabular-nums ${firstFgColor}`}>{firstFgPercent}%</p>
                                <p className="text-slate-400 text-[10px] sm:text-xs uppercase tracking-[0.14em] font-bold mt-1">FG% {firstTeamLabel}</p>
                            </div>
                            <div className="rounded-xl border border-white/10 bg-[#0f1f15] p-3 sm:p-4 text-center">
                                <p className={`text-2xl sm:text-4xl font-black tabular-nums ${secondFgColor}`}>{secondFgPercent}%</p>
                                <p className="text-slate-400 text-[10px] sm:text-xs uppercase tracking-[0.14em] font-bold mt-1">FG% {secondTeamLabel}</p>
                            </div>
                        </div>

                        <div className="space-y-4">
                            {(() => {
                                const pointsInPaint = byOrder(pointsInPaintAguada, pointsInPaintRival);
                                const pointsFromTurnovers = byOrder(pointsFromTurnoversAguada, pointsFromTurnoversRival);
                                const secondChancePoints = byOrder(secondChancePointsAguada, secondChancePointsRival);
                                const fastBreakPoints = byOrder(fastBreakAguada, fastBreakRival);
                                const benchPoints = byOrder(benchPointsAguada, benchPointsRival);

                                return (
                                    <>
                            <ComparisonBar
                                label="Puntos en la pintura"
                                firstValue={pointsInPaint.first}
                                secondValue={pointsInPaint.second}
                                firstTextColorClass={firstTextColorClass}
                                secondTextColorClass={secondTextColorClass}
                                firstBarColorClass={firstBarColorClass}
                                secondBarColorClass={secondBarColorClass}
                            />
                            <ComparisonBar
                                label="Puntos de pérdida"
                                firstValue={pointsFromTurnovers.first}
                                secondValue={pointsFromTurnovers.second}
                                firstTextColorClass={firstTextColorClass}
                                secondTextColorClass={secondTextColorClass}
                                firstBarColorClass={firstBarColorClass}
                                secondBarColorClass={secondBarColorClass}
                            />
                            <ComparisonBar
                                label="Puntos 2da oportunidad"
                                firstValue={secondChancePoints.first}
                                secondValue={secondChancePoints.second}
                                firstTextColorClass={firstTextColorClass}
                                secondTextColorClass={secondTextColorClass}
                                firstBarColorClass={firstBarColorClass}
                                secondBarColorClass={secondBarColorClass}
                            />
                            <ComparisonBar
                                label="Puntos de contraataque"
                                firstValue={fastBreakPoints.first}
                                secondValue={fastBreakPoints.second}
                                firstTextColorClass={firstTextColorClass}
                                secondTextColorClass={secondTextColorClass}
                                firstBarColorClass={firstBarColorClass}
                                secondBarColorClass={secondBarColorClass}
                            />
                            <ComparisonBar
                                label="Puntos banca"
                                firstValue={benchPoints.first}
                                secondValue={benchPoints.second}
                                firstTextColorClass={firstTextColorClass}
                                secondTextColorClass={secondTextColorClass}
                                firstBarColorClass={firstBarColorClass}
                                secondBarColorClass={secondBarColorClass}
                            />
                                    </>
                                );
                            })()}
                        </div>

                        <div className="flex flex-wrap items-center gap-2 text-[10px] sm:text-xs font-bold uppercase tracking-[0.14em] text-slate-300 pt-1">
                            <button
                                type="button"
                                onClick={() => setResultFilter((current) => ({ ...current, made: !current.made }))}
                                className={`inline-flex items-center gap-1.5 sm:gap-2 rounded-md border px-2 sm:px-2.5 py-1 sm:py-1.5 transition-colors ${resultFilter.made ? "border-green-400/60 bg-green-500/15 text-green-200" : "border-white/10 bg-[#0f1f15] text-slate-300 hover:text-white"}`}
                            >
                                <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-green-500" /> Convertido
                            </button>
                            <button
                                type="button"
                                onClick={() => setResultFilter((current) => ({ ...current, missed: !current.missed }))}
                                className={`inline-flex items-center gap-1.5 sm:gap-2 rounded-md border px-2 sm:px-2.5 py-1 sm:py-1.5 transition-colors ${resultFilter.missed ? "border-red-400/60 bg-red-500/15 text-red-200" : "border-white/10 bg-[#0f1f15] text-slate-300 hover:text-white"}`}
                            >
                                <span className="w-2.5 h-2.5 sm:w-3 sm:h-3 rounded-full bg-red-500" /> Errado
                            </button>
                        </div>
                    </aside>
                </div>
            )}
        </section>
    );
}

export default PartidoShotChartSection;
